import {
  ERP_MODULES_CONFIG,
  getModuleById,
  getModuleGroup,
  getModuleIcon,
  getModulesByContext,
  type NavigationContext,
  type Module,
  type ModuleStatus,
} from "@/convex/lib/modules";
import type { modulesTypes } from "@/convex/system/permissions";

/**
 * Filter modules by status
 */
export function filterModulesByStatus(
  modules: Module[],
  status: ModuleStatus | ModuleStatus[],
): Module[] {
  const statuses = Array.isArray(status) ? status : [status];
  return modules.filter((module) =>
    module.navigation?.status
      ? statuses.includes(module.navigation.status)
      : false,
  );
}

/**
 * Filter modules that have tables
 */
export function filterModulesWithTables(modules: Module[]): Module[] {
  return modules.filter((module) => module.tables.length > 0);
}

/**
 * Filter modules by permission
 */
export function filterModulesByPermission(
  modules: Module[],
  permission: "canCreate" | "canRead" | "canUpdate" | "canDelete",
): Module[] {
  return modules.filter((module) => module.permissions.includes(permission));
}

/**
 * Filter modules visible in navigation
 */
export function filterVisibleModules(modules: Module[]): Module[] {
  return modules.filter((module) => module.navigation?.showInNav === true);
}

/**
 * Get modules that match multiple contexts
 */
export function getModulesInMultipleContexts(
  contexts: NavigationContext[],
): Module[] {
  const allContexts = contexts.flatMap((context) =>
    getModulesByContext(context),
  );

  // Remove duplicates by module ID
  const uniqueMap = new Map<string, Module>();
  allContexts.forEach((module) => {
    if (!uniqueMap.has(module.id)) {
      uniqueMap.set(module.id, module);
    }
  });

  return Array.from(uniqueMap.values());
}

/**
 * Get modules that exist in ALL specified contexts
 */
export function getModulesInAllContexts(
  contexts: NavigationContext[],
): Module[] {
  if (contexts.length === 0) return [];

  const firstContext = getModulesByContext(contexts[0]);

  return firstContext.filter((module) =>
    contexts.every((context) => module.navigation?.contexts.includes(context)),
  );
}

/**
 * Search modules by name or description
 */
export function searchModules(modules: Module[], searchTerm: string): Module[] {
  const term = searchTerm.toLowerCase().trim();
  if (!term) return modules;

  return modules.filter(
    (module) =>
      module.name.toLowerCase().includes(term) ||
      module.description.toLowerCase().includes(term) ||
      module.id.toLowerCase().includes(term),
  );
}

/**
 * Sort modules by navigation order
 * Note: When navOrder is context-specific, uses the first available value or 999
 */
export function sortModulesByNavOrder(modules: Module[]): Module[] {
  return [...modules].sort((a, b) => {
    const getOrder = (
      navOrder: number | Partial<Record<string, number>> | undefined,
    ): number => {
      if (navOrder === undefined) return 999;
      if (typeof navOrder === "number") return navOrder;
      // If it's an object, get the first value or 999
      const values = Object.values(navOrder).filter(
        (v): v is number => v !== undefined,
      );
      return values.length > 0 ? values[0] : 999;
    };

    return getOrder(a.navigation?.navOrder) - getOrder(b.navigation?.navOrder);
  });
}

/**
 * Sort modules by name alphabetically
 */
export function sortModulesByName(modules: Module[]): Module[] {
  return [...modules].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get module IDs from modules array
 */
export function getModuleIds(modules: Module[]): string[] {
  return modules.map((m) => m.id);
}

/**
 * Check if a module has a specific route
 */
export function moduleHasRoute(moduleId: string, route: string): boolean {
  const module = getModuleById(moduleId);
  return module ? module.routes.includes(route) : false;
}

/**
 * Get all routes for given modules
 */
export function getAllRoutesForModules(modules: Module[]): string[] {
  const routes = new Set<string>();
  modules.forEach((module) => {
    module.routes.forEach((route) => routes.add(route));
  });
  return Array.from(routes).sort();
}

/**
 * Check if user can perform action on module (based on module permissions config)
 */
export function canPerformAction(
  moduleId: string,
  action: "canCreate" | "canRead" | "canUpdate" | "canDelete",
): boolean {
  const module = getModuleById(moduleId);
  return module ? module.permissions.includes(action) : false;
}

/**
 * Get modules that are read-only (only canRead permission)
 */
export function getReadOnlyModules(modules: Module[]): Module[] {
  return modules.filter(
    (module) =>
      module.permissions.length === 1 && module.permissions.includes("canRead"),
  );
}

/**
 * Get modules with full CRUD permissions
 */
export function getFullCrudModules(modules: Module[]): Module[] {
  const requiredPermissions = [
    "canCreate",
    "canRead",
    "canUpdate",
    "canDelete",
  ];
  return modules.filter((module) =>
    requiredPermissions.every((perm) =>
      module.permissions.includes(perm as any),
    ),
  );
}

/**
 * Group modules by status
 */
export function groupModulesByStatus(
  modules: Module[],
): Record<ModuleStatus, Module[]> {
  const grouped: Record<ModuleStatus, Module[]> = {
    active: [],
    "coming-soon": [],
    deprecated: [],
    inactive: [],
  };

  modules.forEach((module) => {
    const status = module.navigation?.status || "active";
    grouped[status].push(module);
  });

  return grouped;
}

/**
 * Get module by any of its routes
 */
export function getModuleByRoute(route: string): Module | undefined {
  for (const group of Object.values(ERP_MODULES_CONFIG.modules) as any[]) {
    for (const module of group.modules) {
      if (module.routes.includes(route)) {
        return module;
      }
    }
  }

  return undefined;
}

/**
 * Check if a module is in a specific context
 */
export function isModuleInContext(
  moduleId: string,
  context: NavigationContext,
): boolean {
  const module = getModuleById(moduleId);
  return module?.navigation?.contexts.includes(context) || false;
}

/**
 * Get contexts for a specific module
 */
export function getModuleContexts(moduleId: string): NavigationContext[] {
  const module = getModuleById(moduleId);
  return module?.navigation?.contexts || [];
}

/**
 * Validate module ID
 */
export function isValidModuleId(
  moduleId: string,
): moduleId is typeof modulesTypes.type {
  return getModuleById(moduleId) !== undefined;
}

/**
 * Get module display info (name, description, icon)
 */
export function getModuleDisplayInfo(moduleId: string): {
  name: string;
  description: string;
  icon?: string;
} | null {
  const module = getModuleById(moduleId);
  if (!module) return null;

  return {
    name: module.name,
    description: module.description,
    icon: getModuleIcon(moduleId),
  };
}

/**
 * Check if two modules are in the same group
 */
export function areModulesInSameGroup(
  moduleId1: string,
  moduleId2: string,
): boolean {
  const group1 = getModuleGroup(moduleId1);
  const group2 = getModuleGroup(moduleId2);

  return group1?.id === group2?.id;
}
